/*
 * alt-quickview-media.js — ALT-QUICKVIEW-005
 *
 * Makes the in-cart quick-view photo follow the variant, the way the PDP
 * gallery already does. Without this the panel kept painting the first colour's
 * photo whatever swatch was picked, so a shopper choosing Burgundy in the
 * drawer added a pair while looking at the black one.
 *
 * Why Dawn can't do it: product-info.js `updateMedia()` bails unless it finds a
 * <media-gallery ul> in BOTH the fetched section and the panel, and this panel
 * is a single photo on purpose (see sections/cart-quick-view.liquid). Its own
 * `updateSourceFromDestination` list is fixed (price, Sku, Inventory, Volume,
 * Price-Per-Item) and lives in a file the PDP shares, so the media is simply
 * never carried across.
 *
 * Why not reuse altobellos-variant-media.js: that one filters a MULTI-item
 * gallery by alt text and filename, and binds to `document.querySelector(
 * 'product-info')` — the first one on the page, which is never this panel. It
 * has no path for a lone <img>.
 *
 * No request of its own. product-info.js already fetches the whole section on
 * every variant change and hands the parsed document to subscribers on
 * PUB_SUB_EVENTS.variantChange; the server has resolved the correct photo there
 * already (every variant carries its own featured_media). This only moves it
 * across. Same pubsub reuse as pdp-avatar-sync.js.
 *
 * Preloading uses the image's `load` event plus a hard cap, NOT `img.decode()`.
 * A first version used `decode()` and left the photo frozen on the first colour
 * forever, because the promise never settled — reproduced on a detached image
 * and on an already-complete attached one. That was measured in a headless pane
 * that never composites, and `decode()` is specified to settle only once the
 * image is ready to PAINT, so it may well behave normally in a real browser;
 * treat the hang as unconfirmed for real users. Either way `load` is the safer
 * signal (it fired every time in the same conditions, and Dawn itself falls back
 * to it), and the cap guarantees the swap happens even if neither event arrives.
 */
if (!window.__altQuickViewMediaInit) {
  window.__altQuickViewMediaInit = true;

  (function () {
    'use strict';

    var PANEL_ID = 'CartQuickViewInfo';
    // Cap on how long we'll wait for the incoming photo before swapping anyway.
    // Long enough to avoid a blank frame on a warm cache, short enough that a
    // cold or failed image never looks like the panel ignored the click.
    var PRELOAD_CAP_MS = 400;
    // Only the most recent change may commit: clicking Black then Green fires
    // two section fetches, and they can come back in either order.
    var sequence = 0;

    function panelMedia() {
      var panel = document.getElementById(PANEL_ID);
      return panel ? panel.querySelector('.cqv__media') : null;
    }

    function swapQuickViewMedia(event) {
      var data = event && event.data;
      if (!data || !data.html) return;

      var panel = document.getElementById(PANEL_ID);
      if (!panel) return;

      // Only this panel's own variant change. Read the section id off the
      // element rather than hard-coding "cart-quick-view", so renaming the
      // section file doesn't silently stop the photo updating. Mirrors
      // product-info.js's own `get sectionId()`.
      var panelSection = panel.dataset.originalSection || panel.dataset.section;
      if (data.sectionId !== panelSection) return;

      var destination = panel.querySelector('.cqv__media');
      // The PDP's own section carries no .cqv__media, so an event that somehow
      // got past the section check still cannot do anything here.
      var source = data.html.querySelector('.cqv__media');
      if (!destination || !source) return;

      var nextMarkup = source.innerHTML;
      if (nextMarkup.trim() === destination.innerHTML.trim()) return;

      var ticket = ++sequence;
      var committed = false;

      function commit() {
        if (committed || ticket !== sequence) return;
        committed = true;
        // Re-read: the drawer may have closed while the photo was loading.
        var target = panelMedia();
        if (target) target.innerHTML = nextMarkup;
      }

      var nextImage = source.querySelector('img');
      var nextSrc = nextImage && nextImage.getAttribute('src');
      if (!nextSrc) {
        commit();
        return;
      }

      var preload = new Image();
      preload.onload = commit;
      preload.onerror = commit;
      // Same candidate the browser will pick once the markup is in place.
      if (nextImage.getAttribute('sizes')) preload.sizes = nextImage.getAttribute('sizes');
      if (nextImage.getAttribute('srcset')) preload.srcset = nextImage.getAttribute('srcset');
      preload.src = nextSrc;
      setTimeout(commit, PRELOAD_CAP_MS);
    }

    function register() {
      // subscribe/PUB_SUB_EVENTS come from pubsub.js/constants.js, loaded
      // site-wide in layout/theme.liquid.
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      subscribe(PUB_SUB_EVENTS.variantChange, swapQuickViewMedia);
    }

    if (document.readyState === 'complete') {
      register();
    } else {
      window.addEventListener('load', register, { once: true });
    }
  })();
}
